import {
  CardTraderConditionValueType,
  CardTraderLanguageValueType,
} from "magic-shared-types";
import {
  CARD_CONDITIONS_CARDTRADER,
  CARD_LANGUAGES,
  MAPPED_LETTERS_TO_NUMBERS,
  MAPPED_NUMBERS_TO_LETTERS,
} from "./constant";
import { CardSelected, MtgCardTypeComplex } from "./type";
import dayjs from "dayjs";

export const getConditionValue = (value: CardTraderConditionValueType) => {
  return CARD_CONDITIONS_CARDTRADER?.find(
    (condition) => condition.value === value
  );
};

export const getLanguageValue = (value: CardTraderLanguageValueType) => {
  return CARD_LANGUAGES?.find((language) => language.value === value);
};

export const numberToLetters = (number: number) => {
  // Convert the number to a string
  let numberStr = number.toString();

  // Split the number into integer and decimal parts (if any)
  let [integerPart, decimalPart] = numberStr.split(".");

  // Convert the integer part to letters
  const convertedInteger = integerPart
    .split("")
    .map((digit) => MAPPED_NUMBERS_TO_LETTERS[digit] || digit) // Map digits to letters, keep other characters as is
    .join("");

  // If there's a decimal part, process it, otherwise return the integer part
  if (decimalPart) {
    // The decimal part may contain non-numeric characters, so we split into parts and only convert digits
    const convertedDecimal = decimalPart
      .split("")
      .map((digit) => MAPPED_NUMBERS_TO_LETTERS[digit] || digit) // Keep non-digit characters as is
      .join("");
    return `${convertedInteger}.${convertedDecimal}`;
  }

  return convertedInteger;
};

export const lettersToNumber = (letters: string) => {
  // Split the string into the part before and after the decimal point
  const [integerPart, decimalPart] = letters.split(".");

  // Convert the integer part
  const convertedInteger = integerPart
    .split("")
    .map((letter) =>
      MAPPED_LETTERS_TO_NUMBERS[letter] !== undefined
        ? MAPPED_LETTERS_TO_NUMBERS[letter]
        : letter
    ) // Convert letters to numbers, keep other characters intact
    .join("");

  // Convert the decimal part if it exists, otherwise return the integer part
  const convertedDecimal = decimalPart
    ? decimalPart
        .split("")
        .map((letter) =>
          MAPPED_LETTERS_TO_NUMBERS[letter] !== undefined
            ? MAPPED_LETTERS_TO_NUMBERS[letter]
            : letter
        ) // Convert letters to numbers in the decimal part
        .join("")
    : "";

  // If there's a decimal part, return both parts, else just return the integer part
  return convertedDecimal
    ? `${convertedInteger}.${convertedDecimal}`
    : convertedInteger;
};

export function getNumberSeperators(locale: string) {
  const testThousandsNumber = 123456789;
  const thousandsSeperator =
    testThousandsNumber.toLocaleString(locale).replace(/[0-9]/g, "")[0] ?? ",";
  const testDecimalNumber = 0.123;
  const decimalSeperator =
    testDecimalNumber.toLocaleString(locale).replace(/[0-9]/g, "")[0] ?? ".";
  return {
    thousandsSeperator,
    decimalSeperator,
  };
}

export function useAddCommas() {
  const addCommasFn = (number: number | string) => {
    return addCommas(number);
  };
  return addCommasFn;
}

function addCommas(number: number | string) {
  const { decimalSeperator, thousandsSeperator } = getNumberSeperators(
    navigator.language
  );
  const numberString = `${number}`;
  const parts = numberString.split(".");
  let integerPortion = parts[0];
  let decimalPortion = parts.length > 1 ? `${decimalSeperator}${parts[1]}` : "";
  const regex = /(\d+)(\d{3})/;
  while (regex.test(integerPortion)) {
    integerPortion = integerPortion.replace(regex, `$1${thousandsSeperator}$2`);
  }

  return `${integerPortion}${decimalPortion}`;
}

// 🔽 Utility: MTG color sort order
const colorSortOrder = ["W", "U", "B", "R", "G", "artifact", "land"];

// 🔽 Get priority group: mono (1), multi (2), colorless (3)
function getColorGroupPriority(colors?: string[]) {
  if (!colors || colors.length === 0) return 3;
  if (colors.length === 1) return 1;
  return 2;
}

// 🔽 Get index for mono-color order
function getMonoColorIndex(color?: string) {
  return color ? colorSortOrder.indexOf(color) : -1;
}

// Helper to calculate price difference percentage (formatted to 2 decimals)
export function getPriceDifferencePercent(card: CardSelected): number {
  if (
    typeof card.originalPrice === "number" &&
    card.originalPrice !== 0 &&
    card.price !== undefined
  ) {
    const diff = ((card.price - card.originalPrice) / card.originalPrice) * 100;
    return Math.round(diff * 100) / 100; // round to 2 decimals
  }
  return 0;
}

export const handleFilterAndSortCards = ({
  debouncedSearchText,
  cardsBoard,
  sortOption,
  filterOption,
  foundCardComplexArray,
  priceMin,
  priceMax,
  game_id,
  languagesFilter,
  conditionsFilter,
  debouncedSearchTextComment,
}: {
  foundCardComplexArray: Record<number, MtgCardTypeComplex>;
  debouncedSearchText: string;
  cardsBoard: CardSelected[];
  sortOption: string;
  filterOption?: string;
  priceMin?: number;
  priceMax?: number;
  game_id: number;
  languagesFilter: CardTraderLanguageValueType[];
  conditionsFilter: CardTraderConditionValueType[];
  debouncedSearchTextComment: string;
}) => {
  const term = debouncedSearchText.toLowerCase().trim();

  let filtered = cardsBoard.filter((card) => {
    const nameMatch = card?.name?.toLowerCase().includes(term);
    const edition =
      foundCardComplexArray?.[card.id]?.editionCode?.toLowerCase() || "";
    const collectorNumber =
      foundCardComplexArray?.[
        card.id
      ]?.fixed_properties?.collector_number?.toLowerCase() || "";
    const editionCollectorMatch = `${edition} ${collectorNumber}`.includes(
      term
    );
    return nameMatch || editionCollectorMatch;
  });

  filtered = filtered.filter((card) =>
    card.description
      .toLocaleLowerCase()
      .includes(debouncedSearchTextComment.toLowerCase().trim())
  );

  filtered = filtered.filter((card) => card.game_id === game_id);

  if (priceMin !== undefined || priceMax !== undefined) {
    filtered = filtered.filter((card) => {
      const price = card.price ?? 0;
      return (
        (priceMin === undefined || price >= priceMin) &&
        (priceMax === undefined || price <= priceMax)
      );
    });
  }

  if (languagesFilter?.length) {
    filtered = filtered.filter((card) =>
      languagesFilter.includes(card.language)
    );
  }

  if (conditionsFilter?.length) {
    filtered = filtered.filter((card) =>
      conditionsFilter.includes(card.condition)
    );
  }

  // 🔽 Main color sort comparator
  function sortByColorIdentity(a: CardSelected, b: CardSelected) {
    const aColors = foundCardComplexArray[a.id]?.colors || [];
    const bColors = foundCardComplexArray[b.id]?.colors || [];

    const groupA = getColorGroupPriority(aColors);
    const groupB = getColorGroupPriority(bColors);

    if (groupA !== groupB) return groupA - groupB;

    // Monocolor sort by W > U > B > R > G
    if (groupA === 1) {
      return getMonoColorIndex(aColors[0]) - getMonoColorIndex(bColors[0]);
    }

    // Multicolor: sort by joined string (alphabetical)
    if (groupA === 2) {
      return aColors.join("").localeCompare(bColors.join(""));
    }

    return 0;
  }

  filtered.reverse();

  // 🔽 Apply sorting
  switch (sortOption) {
    case "name-asc":
      filtered.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case "name-desc":
      filtered.sort((a, b) => b.name.localeCompare(a.name));
      break;
    case "price-asc":
      filtered.sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
      break;
    case "price-desc":
      filtered.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
      break;
    case "price-diff-asc":
      filtered.sort(
        (a, b) => getPriceDifferencePercent(a) - getPriceDifferencePercent(b)
      );
      break;
    case "price-diff-desc":
      filtered.sort(
        (a, b) => getPriceDifferencePercent(b) - getPriceDifferencePercent(a)
      );
      break;
    case "color":
      filtered.sort(sortByColorIdentity);
      break;
    case "date-asc":
      filtered.sort((a, b) => {
        const dateA = a.date ? dayjs(a.date) : dayjs();
        const dateB = b.date ? dayjs(b.date) : dayjs();

        if (!dateA || !dateA.isValid()) return 1; // put cards without date last
        if (!dateB || !dateB.isValid()) return -1;

        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
      });
      break;

    case "date-desc":
      filtered.sort((a, b) => {
        const dateA = a.date ? dayjs(a.date) : dayjs();
        const dateB = b.date ? dayjs(b.date) : dayjs();

        if (!dateA || !dateA.isValid()) return 1;
        if (!dateB || !dateB.isValid()) return -1;

        return dateA.isAfter(dateB) ? -1 : dateA.isBefore(dateB) ? 1 : 0;
      });
      break;
    default:
      break;
  }

  if (filterOption) {
    if (filterOption === "only-edited") {
      filtered = filtered.filter((card) => card.hasBeenEdited);
    }
    if (filterOption === "not-edited") {
      filtered = filtered.filter((card) => !card.hasBeenEdited);
    }
    if (filterOption === "pricing-error") {
      filtered = filtered.filter((card) => card.hasPriceIssue);
    }
  }

  return filtered;
};
